import clsx from "clsx";
import { MISSING_VALUE, formatNumber, formatPercentNumber, type NumericInput } from "@/lib/format";
import styles from "./Quantity.module.css";

/** Default decimals per unit: masses at the 1 mg balance resolution, percentages at 2 dp. */
const DEFAULT_DECIMALS: Record<string, number> = { g: 3, mg: 1, kg: 4, "%": 2, ml: 2, mm: 1 };

export interface QuantityProps {
  /** A decimal string from the API; null/undefined renders "—" (missing is never zero). */
  value: NumericInput;
  /** Unit shown after the value in the subtle text color ("g", "%", "mm"). Omit when the column header carries it. */
  unit?: string;
  /** Fixed decimals; defaults by unit (g: 3, %: 2). */
  decimals?: number;
  /** The value is a 0–1 fraction to show as a percentage (implies unit "%"). */
  fromFraction?: boolean;
  /** "exceptZero" for deltas (+0.120 g, −0.034 g). */
  signDisplay?: "auto" | "always" | "exceptZero";
  /** Hide the unit visually but keep it for screen readers (unit in the column header). */
  hideUnit?: boolean;
  className?: string;
}

/** A decimal quantity with its unit: 12.346 g, 15.00 %. Tabular figures; never wraps. */
export function Quantity({
  value,
  unit,
  decimals,
  fromFraction = false,
  signDisplay,
  hideUnit = false,
  className,
}: QuantityProps) {
  const displayUnit = fromFraction ? "%" : unit;
  const places = decimals ?? (displayUnit ? DEFAULT_DECIMALS[displayUnit] : undefined);
  const number =
    displayUnit === "%"
      ? formatPercentNumber(value, { from: fromFraction ? "fraction" : "percent", decimals: places, signDisplay })
      : formatNumber(value, places === undefined ? { maxDecimals: 3, signDisplay } : { decimals: places, signDisplay });
  const missing = number === MISSING_VALUE;
  return (
    <span className={clsx(styles.quantity, className)} data-missing={missing || undefined}>
      <span>{number}</span>
      {displayUnit && !missing ? (
        <span className={hideUnit ? styles.hiddenUnit : styles.unit}>{displayUnit}</span>
      ) : null}
    </span>
  );
}
